export const performanceStorageKey = "__wombat.performance.enabled";

/** Shared, opt-in User Timing measurements; disabled calls never read the clock. */
class PerformanceTracker {
  private profilingEnabled = false;

  constructor() {
    try {
      this.profilingEnabled =
        globalThis.localStorage?.getItem(performanceStorageKey) === "true";
    } catch {
      // Storage may be unavailable in restricted browser contexts.
    }
  }

  get enabled(): boolean {
    return this.profilingEnabled;
  }

  enable(): void {
    this.setEnabled(true);
  }

  disable(): void {
    this.setEnabled(false);
  }

  private setEnabled(enabled: boolean): void {
    this.profilingEnabled = enabled;
    try {
      globalThis.localStorage?.setItem(performanceStorageKey, String(enabled));
    } catch {
      // Runtime toggling still works when persistence is unavailable.
    }
  }

  private record(name: string, start: number): void {
    try {
      performance.measure(name, { start, end: performance.now() });
    } catch {
      // Diagnostics must not change application results or mask errors.
    }
  }

  measure<T>(name: string, work: () => T): T {
    if (!this.profilingEnabled) {
      return work();
    }
    const start = performance.now();
    try {
      return work();
    } finally {
      this.record(name, start);
    }
  }

  async measureAsync<T>(name: string, work: () => Promise<T>): Promise<T> {
    if (!this.profilingEnabled) {
      return work();
    }
    const start = performance.now();
    try {
      return await work();
    } finally {
      this.record(name, start);
    }
  }
}

export const performanceTracker = new PerformanceTracker();
